import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Type } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StoreButton } from "@/components/Buttons";
import { uploadArtwork } from "@/components/signs/useSignConfig";

const DPI = 300;

async function renderRoster({ name, number, nameHeightIn, numberHeightIn, color }) {
    await document.fonts.load('400 200px Anton');
    const pad = Math.round(0.08 * DPI);
    const nameFont = Math.round(nameHeightIn * DPI);
    const numFont = Math.round(numberHeightIn * DPI);
    const measure = document.createElement("canvas").getContext("2d");

    let width = 0, height = pad * 2;
    if (name) {
        measure.font = `${nameFont}px Anton`;
        width = Math.max(width, measure.measureText(name).width);
        height += nameFont * 1.05;
    }
    if (number) {
        measure.font = `${numFont}px Anton`;
        width = Math.max(width, measure.measureText(number).width);
        height += numFont * 1.05 + (name ? pad : 0);
    }
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(width + pad * 2);
    canvas.height = Math.ceil(height);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    let y = pad;
    if (name) {
        ctx.font = `${nameFont}px Anton`;
        ctx.fillText(name, canvas.width / 2, y);
        y += nameFont * 1.05 + (number ? pad : 0);
    }
    if (number) {
        ctx.font = `${numFont}px Anton`;
        ctx.fillText(number, canvas.width / 2, y);
    }
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    return { blob, w: canvas.width, h: canvas.height };
}

function Pill({ on, theme, children, ...props }) {
    return (
        <button type="button" {...props}
            className="rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:bg-zinc-50"
            style={on ? { borderColor: theme?.primary, background: theme?.primary, color: theme?.on } : { borderColor: "#e4e4e7", color: "#3f3f46" }}>
            {children}
        </button>
    );
}

const Label = ({ children, hint }) => (
    <div className="mb-1.5 flex items-baseline justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{children}</p>
        {hint && <span className="text-[11px] text-zinc-500">{hint}</span>}
    </div>
);

export default function NamesNumbers({ sheet, theme }) {
    const [open, setOpen] = useState(false);
    const [roster, setRoster] = useState("SMITH 23\nJOHNSON 7\nWILLIAMS 12");
    const [mode, setMode] = useState("both");
    const [nameHeight, setNameHeight] = useState(2);
    const [numberHeight, setNumberHeight] = useState(6);
    const [color, setColor] = useState("#111111");
    const [busy, setBusy] = useState(false);

    const generate = async () => {
        const lines = roster.split("\n").map((l) => l.trim()).filter(Boolean);
        if (!lines.length) return;
        setBusy(true);
        try {
            for (const line of lines) {
                const m = line.match(/^(.*?)[\s]*(\d+)?$/);
                const rawName = (m?.[1] || line).trim().toUpperCase();
                const rawNumber = (m?.[2] || "").trim();
                const name = mode === "numbers" ? "" : rawName;
                const number = mode === "names" ? "" : rawNumber;
                if (!name && !number) continue;
                const { blob, w, h } = await renderRoster({
                    name, number, nameHeightIn: nameHeight, numberHeightIn: numberHeight, color,
                });
                const file = new File([blob], `${(name || number).replace(/\s+/g, "-")}.png`, { type: "image/png" });
                const rec = await uploadArtwork(file);
                const ratio = w / h;
                const wIn = Math.min(sheet.film.printable_width_inches, Math.floor((w / DPI) * 100) / 100);
                sheet.addItem({
                    fileId: rec.id, file: rec, url: URL.createObjectURL(blob), name: file.name,
                    ratio, px: { w, h }, wIn, hIn: Math.floor((wIn / ratio) * 100) / 100,
                });
            }
            toast.success(`${lines.length} roster items added`);
            setOpen(false);
        } catch (e) {
            toast.error("Could not build the roster");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button type="button" data-testid="open-names-numbers"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-bold text-zinc-800 transition-colors hover:bg-zinc-50">
                    <Type size={14} style={{ color: theme?.primary }} /> Names & numbers
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="names-numbers-dialog">
                <DialogHeader><DialogTitle className="font-display text-2xl font-black">Names & numbers</DialogTitle></DialogHeader>
                <p className="text-sm text-zinc-500">One per line — name then number, like <span className="font-mono text-zinc-800">SMITH 23</span>. Bold athletic block font, print-ready at 300 dpi.</p>

                <textarea value={roster} onChange={(e) => setRoster(e.target.value)} rows={6} data-testid="roster-input"
                    className="mt-1 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2.5 font-mono text-sm text-zinc-900 outline-none"
                    onFocus={(e) => (e.target.style.borderColor = theme?.primary)} onBlur={(e) => (e.target.style.borderColor = "")} />

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label>Include</Label>
                        <div className="flex flex-wrap gap-1.5">
                            <Pill on={mode === "both"} theme={theme} onClick={() => setMode("both")} data-testid="roster-mode-both">Name + number</Pill>
                            <Pill on={mode === "names"} theme={theme} onClick={() => setMode("names")} data-testid="roster-mode-names">Names</Pill>
                            <Pill on={mode === "numbers"} theme={theme} onClick={() => setMode("numbers")} data-testid="roster-mode-numbers">Numbers</Pill>
                        </div>
                    </div>
                    <div>
                        <Label>Ink color</Label>
                        <div className="flex gap-1.5">
                            <Pill on={color === "#111111"} theme={theme} onClick={() => setColor("#111111")} data-testid="roster-color-black">Black</Pill>
                            <Pill on={color === "#ffffff"} theme={theme} onClick={() => setColor("#ffffff")} data-testid="roster-color-white">White</Pill>
                        </div>
                    </div>
                    {mode !== "numbers" && (
                        <div>
                            <Label hint={`${nameHeight}"`}>Name height</Label>
                            <input type="range" min="1" max="5" step="0.25" value={nameHeight} onChange={(e) => setNameHeight(Number(e.target.value))}
                                className="w-full" style={{ accentColor: theme?.primary }} data-testid="roster-name-height" />
                        </div>
                    )}
                    {mode !== "names" && (
                        <div>
                            <Label hint={`${numberHeight}"`}>Number height</Label>
                            <input type="range" min="2" max="12" step="0.5" value={numberHeight} onChange={(e) => setNumberHeight(Number(e.target.value))}
                                className="w-full" style={{ accentColor: theme?.primary }} data-testid="roster-number-height" />
                        </div>
                    )}
                </div>

                <StoreButton theme={theme} onClick={generate} disabled={busy} className="mt-1 w-full py-2.5 text-xs" data-testid="roster-generate">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <Type size={16} />} Add to sheet
                </StoreButton>
            </DialogContent>
        </Dialog>
    );
}
